import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select, { MultiValue, StylesConfig } from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  getCachedCollection, 
  setCachedDocument, 
  deleteCachedDocument,
  updateCachedDocument,
  logQuery,
  getTeacherUsers,
  getUsersClasses
} from '../utils/firebaseUtils';
import { Timestamp, deleteField } from 'firebase/firestore';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../translations';
import { styles, classNames } from '../styles/styleUtils';
import { getDayName } from '../utils/dateUtils';
import Modal from '../components/Modal';
import { ClassSchedule } from '../types/interfaces';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { invalidateCalendarCache } from '../services/calendarService';
import { PlusIcon, TrashIcon, PencilIcon, InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { formatTimeWithTimezones } from '../utils/dateUtils';
import { Class, SelectOption, User } from '../types/interfaces';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import { Tooltip } from '../components/Tooltip';
import { archiveClassConfigVersion } from '../utils/classConfigVersionUtils';

/**
 * Helper function to check if schedule-related fields have changed.
 * Used to determine if we need to archive the old config.
 */
const hasScheduleChanged = (
  originalClass: Class,
  editedClass: any
): boolean => {
  // Check schedule type
  if (originalClass.scheduleType !== editedClass.scheduleType) return true;
  
  // Check single schedule fields
  if (originalClass.dayOfWeek !== editedClass.dayOfWeek) return true;
  if (originalClass.startTime !== editedClass.startTime) return true;
  if (originalClass.endTime !== editedClass.endTime) return true;
  if (originalClass.timezone !== editedClass.timezone) return true;
  
  // Check frequency
  const origFreq = originalClass.frequency || { type: 'weekly', every: 1 };
  const editFreq = editedClass.frequency || { type: 'weekly', every: 1 };
  if (origFreq.type !== editFreq.type || origFreq.every !== editFreq.every) return true;
  
  // Check multiple schedules
  const origSchedules = originalClass.schedules || [];
  const editSchedules = editedClass.schedules || [];
  
  if (origSchedules.length !== editSchedules.length) return true;
  
  // Compare each schedule
  for (let i = 0; i < origSchedules.length; i++) {
    const orig = origSchedules[i];
    const edit = editSchedules[i];
    if (!edit) return true;
    if (orig.dayOfWeek !== edit.dayOfWeek) return true;
    if (orig.startTime !== edit.startTime) return true;
    if (orig.endTime !== edit.endTime) return true;
    if ((orig.timezone || originalClass.timezone) !== (edit.timezone || editedClass.timezone)) return true;
  }
  
  return false;
};

/**
 * Helper function to check if payment config fields have changed.
 */
const hasPaymentConfigChanged = (
  originalClass: Class,
  editedClass: any
): boolean => {
  const orig = originalClass.paymentConfig || ({} as Class['paymentConfig']);
  const edit = editedClass.paymentConfig || {};

  if (orig.type !== edit.type) return true;
  if ((orig.weeklyInterval || null) !== (edit.weeklyInterval || null)) return true;
  if ((orig.monthlyOption || null) !== (edit.monthlyOption || null)) return true;
  if ((orig.startDate || null) !== (edit.startDate || null)) return true;
  if ((orig.amount ?? null) !== (edit.amount ?? null)) return true;
  if ((orig.currency || null) !== (edit.currency || null)) return true;
  if ((orig.paymentLink || null) !== (edit.paymentLink || null)) return true;

  return false;
};

type SelectStyles = StylesConfig<SelectOption, true>;

const generateTimeOptions = () => {
  const times = [];
  // Start from 6 AM and go until 9 PM
  for (let hour = 6; hour <= 21; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = new Date();
      time.setHours(hour);
      time.setMinutes(minute);
      // Format consistently as "hh:mm AM/PM"
      const formattedTime = time.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
      });
      times.push(formattedTime);
    }
  }
  return times;
};

// Common timezones for the dropdown
const getTimezoneOptions = () => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Common timezones with descriptive labels
  const commonTimezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET) - New York' },
    { value: 'America/Chicago', label: 'Central Time (CT) - Chicago' },
    { value: 'America/Denver', label: 'Mountain Time (MT) - Denver' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles' },
    { value: 'America/Sao_Paulo', label: 'Brasília Time (BRT) - São Paulo' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT) - London' },
    { value: 'Europe/Paris', label: 'Central European Time (CET) - Paris' },
    { value: 'Europe/Moscow', label: 'Moscow Time (MSK) - Moscow' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST) - Tokyo' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (CST) - Shanghai' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET) - Sydney' },
  ];
  
  // Check if user's timezone is already in the list
  const userTimezoneIndex = commonTimezones.findIndex(tz => tz.value === userTimezone);
  let result = [...commonTimezones];
  
  if (userTimezoneIndex !== -1) {
    // If user's timezone is in the list, modify its label and move it to the top
    const userTimezoneOption = { 
      ...result[userTimezoneIndex],
      label: `${result[userTimezoneIndex].label} (Your timezone)`
    };
    
    // Remove it from its current position
    result.splice(userTimezoneIndex, 1);
    
    // Add it to the top of the list
    result.unshift(userTimezoneOption);
  } else {
    // If user's timezone is not in the list, add it to the top
    result.unshift({
      value: userTimezone,
      label: `${userTimezone} (Your timezone)`
    });
  }
  
  return result;
};

const getNextDayOccurrence = (dayOfWeek: number) => {
  const today = new Date();
  const resultDate = new Date();
  const daysUntilNext = (dayOfWeek - today.getDay() + 7) % 7;
  resultDate.setDate(today.getDate() + daysUntilNext);
  // Reset time to start of day
  resultDate.setHours(0, 0, 0, 0);
  return resultDate;
};

const timeOptions = generateTimeOptions();

export const AdminSchedule = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const DAYS_OF_WEEK = [t.sunday, t.monday, t.tuesday, t.wednesday, t.thursday, t.friday, t.saturday];
  const [classes, setClasses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<any>(null);
  const [deletingClassId, setDeletingClassId] = useState<string | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [editContractFile, setEditContractFile] = useState<File | null>(null);
  const [newClass, setNewClass] = useState<any>({
    scheduleType: 'multiple',
    dayOfWeek: 1,
    startTime: '09:00 AM',
    endTime: '10:00 AM',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Use user's local timezone
    schedules: [],
    courseType: 'Individual',
    studentEmails: [],
    startDate: getNextDayOccurrence(1),
    endDate: null,
    contractUrl: '',
    paymentConfig: {
      type: 'weekly',
      weeklyInterval: 1,
      monthlyOption: null,
      startDate: new Date().toISOString().split('T')[0],
      paymentLink: '',
      amount: 0,
      currency: 'USD'
    },
    frequency: {
      type: 'custom',
      every: 1
    }
  });
  const [showMobileView, setShowMobileView] = useState(window.innerWidth < 768);
  const [currentStep, setCurrentStep] = useState<'schedule' | 'payment'>('schedule');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [classToDelete, setClassToDelete] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<'dayTime' | 'students' | 'amount'>('dayTime');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { currentUser } = useAuth();
  const { isAdmin } = useAdmin();

  useEffect(() => {
    
    const loadData = async () => {
      setLoading(true);
      try {
        logQuery('Initial data load');
        await fetchAllUsers();
        await fetchClasses();
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Check if calendar was invalidated while we were away
        const shouldRefresh = sessionStorage.getItem('calendar-invalidated');
        if (shouldRefresh) {
          logQuery('Refreshing data after calendar invalidation (bypassing cache)');
          sessionStorage.removeItem('calendar-invalidated');
          try {
            await fetchAllUsers();
            await fetchClasses(true); // Bypass cache to get fresh data
          } catch (error) {
            console.error('Error refreshing data:', error);
          }
        }
      }
    };

    // Listen for calendar invalidation events
    const handleCalendarInvalidated = async () => {
      logQuery('Calendar invalidated - refreshing classes immediately');
      sessionStorage.setItem('calendar-invalidated', 'true');
      try {
        // Force a full refresh with cache bypass
        setLoading(true);
        await fetchAllUsers();
        await fetchClasses(true);
      } catch (error) {
        console.error('Error refreshing after invalidation:', error);
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener('calendar-invalidated', handleCalendarInvalidated as EventListener);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleResize = () => {
      setShowMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    // Check if we need to refresh due to calendar invalidation before initial load
    const wasInvalidated = sessionStorage.getItem('calendar-invalidated');
    if (wasInvalidated) {
      sessionStorage.removeItem('calendar-invalidated');
    }

    // Only load data if we have a current user
    if (currentUser?.uid) {
      // If calendar was invalidated, force bypass cache on initial load
      if (wasInvalidated) {
        setLoading(true);
        Promise.all([fetchAllUsers(), fetchClasses(true)])
          .catch((error) => {
            console.error('Error loading data:', error);
            toast.error('Failed to load data');
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        loadData();
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('calendar-invalidated', handleCalendarInvalidated as EventListener);
    };
  }, [currentUser?.uid]); // Add currentUser?.uid as dependency

  const fetchClasses = async (bypassCache: boolean = false) => {
    if (!currentUser?.uid) {
      setClasses([]);
      return;
    }

    try {
      if (isAdmin) {
        logQuery('Querying classes as admin', { userId: currentUser.uid, bypassCache });
        const classesData = await getCachedCollection<Class>('classes', [], { 
          userId: currentUser.uid,
          includeIds: true,
          bypassCache
        });
        logQuery('Admin classes query result', { count: classesData.length });
        setClasses(classesData);
      } else {
        // For teachers, first get their students
        logQuery('Fetching teacher users', { teacherAuthId: currentUser.uid });
        const students = await getTeacherUsers<User>(currentUser.uid, {
          userId: currentUser.uid,
          bypassCache
        });
        logQuery('Teacher users query result', { count: students.length });

        if (students.length > 0) {
          // Get all classes for these students
          const studentEmails = students.map(student => student.email);
          logQuery('Fetching classes for students', { studentCount: studentEmails.length });
          const classes = await getUsersClasses(studentEmails, {
            userId: currentUser.uid,
            bypassCache
          });
          logQuery('Classes query result', { count: classes.length });
          setClasses(classes);
        } else {
          setClasses([]);
        }
      }
    } catch (error) {
      logQuery('Error fetching classes', error);
      throw error;
    }
  };

  const fetchAllUsers = async () => {
    if (!currentUser?.uid) {
      setUsers([]);
      return;
    }

    try {
      let usersList: User[] = [];
      if (isAdmin) {
        logQuery('Querying all users as admin', { userId: currentUser.uid });
        // If admin, get all users
        usersList = await getCachedCollection<User>('users', [], { 
          userId: currentUser.uid
        });
        logQuery('Admin users query result', { count: usersList.length });
      } else {
        logQuery('Fetching teacher users', { teacherAuthId: currentUser.uid });
        // If teacher, only get their students
        usersList = await getTeacherUsers<User>(currentUser.uid, {
          userId: currentUser.uid
        });
        logQuery('Teacher users query result', { count: usersList.length });
      }

      setUsers(usersList);
    } catch (error) {
      logQuery('Error fetching users', error);
      throw error;
    }
  };

  // Add these functions to handle schedule management
  const handleAddSchedule = () => {
    // Get the time options for 9:00 AM and 10:00 AM
    const defaultStartTime = timeOptions.find(time => time.includes('9:00') && time.includes('AM')) || '09:00 AM';
    const defaultEndTime = timeOptions.find(time => time.includes('10:00') && time.includes('AM')) || '10:00 AM';
    
    setNewClass((prev: typeof newClass) => {
      // Default to Sunday (0) if no schedules exist yet, otherwise use the next day after the last added day
      const lastDayOfWeek = prev.schedules.length > 0 
        ? prev.schedules[prev.schedules.length - 1].dayOfWeek 
        : -1;
      
      // Choose the next day that isn't already in the schedules
      let newDayOfWeek = (lastDayOfWeek + 1) % 7;
      const existingDays = prev.schedules.map((s: ClassSchedule) => s.dayOfWeek);
      
      // Find the first day that isn't already scheduled
      while (existingDays.includes(newDayOfWeek)) {
        newDayOfWeek = (newDayOfWeek + 1) % 7;
      }
      
      const newSchedule = { 
        dayOfWeek: newDayOfWeek, 
        startTime: defaultStartTime, 
        endTime: defaultEndTime,
        timezone: prev.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone // Use local timezone as fallback
      };
      
      const updatedSchedules = [...prev.schedules, newSchedule];
      
      return {
        ...prev,
        schedules: updatedSchedules,
        timezone: prev.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone // Use local timezone as fallback for parent object too
      };
    });
  };

  const handleRemoveSchedule = (index: number) => {
    setNewClass((prev: typeof newClass) => {
      // Get the schedule that's being removed
      const scheduleToRemove = prev.schedules[index];
      
      // Filter out the schedule at the specified index
      const updatedSchedules = prev.schedules.filter((_: any, i: number) => i !== index);
      
      // If we're removing the last schedule, return to single day mode
      if (updatedSchedules.length === 0) {
        return {
          ...prev,
          scheduleType: 'single',
          schedules: []
        };
      }
      
      // Check if the current start date's day of week matches the day being removed
      const currentStartDateDay = prev.startDate.getDay();
      const isRemovingCurrentDay = scheduleToRemove.dayOfWeek === currentStartDateDay;
      
      // Check if the day being removed is the only instance of that day in the schedules
      const remainingDaysOfWeek = updatedSchedules.map((s: ClassSchedule) => s.dayOfWeek);
      const dayStillExists = remainingDaysOfWeek.includes(currentStartDateDay);
      
      // If we're removing the day that matches the current start date and it doesn't exist in other schedules
      if (isRemovingCurrentDay && !dayStillExists && updatedSchedules.length > 0) {
        // Update the start date to the next occurrence of the first remaining day
        const nextOccurrence = getNextDayOccurrence(updatedSchedules[0].dayOfWeek);
        
        return {
          ...prev,
          schedules: updatedSchedules,
          startDate: nextOccurrence,
          // Reset end date if it's now before the start date
          ...(prev.endDate && prev.endDate < nextOccurrence ? { endDate: null } : {})
        };
      }
      
      return {
        ...prev,
        schedules: updatedSchedules
      };
    });
  };

  const handleScheduleChange = (index: number, field: keyof ClassSchedule, value: string | number) => {
    setNewClass((prev: typeof newClass) => {
      const updatedSchedules = [...prev.schedules];
      updatedSchedules[index] = {
        ...updatedSchedules[index],
        [field]: value,
        // Always ensure timezone is set when making any schedule changes
        timezone: updatedSchedules[index].timezone || prev.timezone
      };
      
      // If changing day of week, update start/end times if needed
      if (field === 'dayOfWeek') {
        // When day of week is changed, we need to update the start date if it's currently
        // set to a day that doesn't match any of the schedules
        const newDayOfWeek = value as number;
        
        // Get all selected days of week from schedules (including the newly updated one)
        const selectedDays = updatedSchedules.map(schedule => schedule.dayOfWeek);
        
        // Check if the current start date's day of week is in the selected days
        const currentStartDateDay = prev.startDate.getDay();
        const isCurrentStartDateValid = selectedDays.includes(currentStartDateDay);
        
        // If the current start date is not valid anymore, update it to the next occurrence of the new day
        if (!isCurrentStartDateValid) {
          const nextOccurrence = getNextDayOccurrence(newDayOfWeek);
          
          return {
            ...prev,
            schedules: updatedSchedules,
            startDate: nextOccurrence,
            // Reset end date if it's now before the start date
            ...(prev.endDate && prev.endDate < nextOccurrence ? { endDate: null } : {})
          };
        }
      } else if (field === 'startTime') {
        // Parse the selected start time
        const [time, period] = value.toString().split(' ');
        const [hours, minutes] = time.split(':').map(Number);
        
        // Create Date objects for start and end times
        const startDate = new Date();
        startDate.setHours(period === 'PM' && hours !== 12 ? hours + 12 : (period === 'AM' && hours === 12 ? 0 : hours), minutes);
        
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
        
        // Format end time in 12-hour format
        const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        
        updatedSchedules[index].endTime = endTime;
      } else if (field === 'endTime') {
        // Parse both times
        const [startTime, startPeriod] = updatedSchedules[index].startTime.split(' ');
        const [endTime, endPeriod] = value.toString().split(' ');
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);

        // Create Date objects for comparison
        const startDate = new Date();
        const endDate = new Date();
        
        startDate.setHours(
          startPeriod === 'PM' && startHours !== 12 ? startHours + 12 : (startPeriod === 'AM' && startHours === 12 ? 0 : startHours),
          startMinutes
        );
        endDate.setHours(
          endPeriod === 'PM' && endHours !== 12 ? endHours + 12 : (endPeriod === 'AM' && endHours === 12 ? 0 : endHours),
          endMinutes
        );

        // If end time is before start time, adjust start time to be 1 hour before end time
        if (endDate <= startDate) {
          const newStartDate = new Date(endDate.getTime() - 60 * 60 * 1000);
          const newStartTime = newStartDate.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: true 
          });
          
          updatedSchedules[index].startTime = newStartTime;
        }
      }
      
      return {
        ...prev,
        schedules: updatedSchedules
      };
    });
  };

  const handleCreateClass = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    let contractUrl = '';

    try {
      // Handle contract file upload if provided
      if (contractFile) {
        const cleanFileName = contractFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const timestamp = Date.now();
        const classId = timestamp.toString(); // Using timestamp as classId since we use it below
        const finalFileName = `${timestamp}_${cleanFileName}`;
        const storageRef = ref(storage, `contracts/${classId}/${finalFileName}`);
        
        // Upload with metadata
        const metadata = {
          contentType: contractFile.type,
          customMetadata: {
            originalName: contractFile.name,
            uploadedAt: new Date().toISOString()
          }
        };
        
        logQuery('Uploading contract file', { filename: finalFileName, metadata });
        await uploadBytes(storageRef, contractFile, metadata);
        logQuery('Getting contract file download URL', { filename: finalFileName });
        contractUrl = await getDownloadURL(storageRef);
      }

      // Create the class document
      const classToCreate = {
        ...newClass,
        contractUrl,
        timezone: newClass.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone, // Use local timezone as fallback
        schedules: newClass.schedules.map((schedule: any) => ({
          ...schedule,
          timezone: schedule.timezone || newClass.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone // Use local timezone as fallback
        })),
        startDate: Timestamp.fromDate(newClass.startDate),
        endDate: newClass.endDate ? Timestamp.fromDate(newClass.endDate) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        // Config applies from class start so past occurrences in the class range are covered
        configEffectiveFrom: Timestamp.fromDate(newClass.startDate),
      };

      logQuery('Creating new class', classToCreate);
      const classId = Date.now().toString();
      
      // Create the class document
      await setCachedDocument('classes', classId, classToCreate, {
        userId: currentUser?.uid
      });
      
      logQuery('Invalidating calendar cache');
      await invalidateCalendarCache();

      // Add the new class to the local state immediately
      const createdClass = { id: classId, ...classToCreate };
      setClasses(prevClasses => [...prevClasses, createdClass]);

      // Reset form
      setNewClass({
        scheduleType: 'multiple',
        dayOfWeek: 1,
        startTime: '09:00 AM',
        endTime: '10:00 AM',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Use user's local timezone
        schedules: [],
        courseType: 'Individual',
        studentEmails: [],
        startDate: getNextDayOccurrence(1), // Start date is next Monday
        endDate: null,
        contractUrl: '',
        paymentConfig: {
          type: 'weekly',
          weeklyInterval: 1,
          monthlyOption: null,
          startDate: new Date().toISOString().split('T')[0],
          paymentLink: '',
          amount: 0,
          currency: 'USD'
        },
        frequency: {
          type: 'custom',
          every: 1
        }
      });
      setContractFile(null);
      
      toast.success(t.admin.schedule.success.classCreated);
      
      // Close modal and reset step
      setIsModalOpen(false);
      setCurrentStep('schedule');
    } catch (error) {
      logQuery('Error creating class', error);
      toast.error(t.admin.schedule.errors.createClass);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClass = async (classId: string) => {
    try {
      setDeletingClassId(classId);
      logQuery('Deleting class', { classId });
      await deleteCachedDocument('classes', classId);
      logQuery('Invalidating calendar cache');
      await invalidateCalendarCache();
      
      setClasses(prevClasses => prevClasses.filter(c => c.id !== classId));
      toast.success(t.admin.schedule.success.classDeleted);
    } catch (error) {
      logQuery('Error deleting class', error);
      toast.error(t.admin.schedule.errors.deleteClass);
    } finally {
      setDeletingClassId(null);
      setClassToDelete(null);
      setShowDeleteConfirmation(false);
    }
  };

  const confirmDeleteClass = (classId: string) => {
    setClassToDelete(classId);
    setShowDeleteConfirmation(true);
  };

  const cancelDeleteClass = () => {
    setClassToDelete(null);
    setShowDeleteConfirmation(false);
  };

  const studentOptions = users
    .filter(user => !user.isAdmin) // Filter out admin users
    .map(user => ({
      value: user.email,
      label: `${user.name} ${user.status === 'pending' ? '(Pending)' : ''}`
    }));

  const customSelectStyles: SelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      background: 'white',
      borderRadius: '0.375rem',
      borderColor: state.isFocused ? '#6366F1' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 1px #6366F1' : 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: state.isFocused ? '#6366F1' : '#9CA3AF'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 8px',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6B7280',
      fontSize: '0.875rem'
    }),
    input: (base) => ({
      ...base,
      color: '#111827',
      margin: 0,
      padding: 0,
      opacity: 1,
      height: 'auto',
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '5px 8px',
      color: '#6B7280',
      '&:hover': {
        color: '#374151'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '5px 8px',
      color: '#6B7280',
      '&:hover': {
        color: '#374151'
      }
    }),
    menu: (base) => ({
      ...base,
      marginTop: '1px',
      maxHeight: '300px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 50
    }),
    option: (base, state) => ({
      ...base,
      color: '#111827',
      padding: '8px 12px',
      backgroundColor: state.isFocused ? '#EEF2FF' : state.isSelected ? '#E0E7FF' : 'white',
      '&:active': {
        backgroundColor: '#C7D2FE',
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#EEF2FF',
      border: '1px solid #C7D2FE',
      borderRadius: '0.25rem'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#4338CA',
      padding: '2px 6px',
      fontSize: '0.875rem'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#4338CA',
      padding: '0 4px',
      '&:hover': {
        backgroundColor: '#C7D2FE',
        color: '#312E81'
      }
    })
  };

  const handleStudentChange = (selected: MultiValue<SelectOption>) => {
    setNewClass((prev: typeof newClass) => ({
      ...prev,
      studentEmails: selected.map(option => option.value)
    }));
  };

  // Function to handle opening the edit modal
  const openEditModal = (classItem: Class) => {
    // For multiple schedules class, extract the schedules array
    const schedules = classItem.schedules?.map((schedule: ClassSchedule) => ({
      ...schedule,
      // Add timezone to schedule if missing
      timezone: schedule.timezone || classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone // Use local timezone as default
    })) || [];
    
    // For start date and end date, convert Timestamp to Date
    const startDate = classItem.startDate.toDate();
    
    // Ensure the timezone is set
    const timezone = classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone; // Use local timezone as default
    
    // Format monthly option for UI
    let monthlyOption = classItem.paymentConfig?.monthlyOption || null;
    
    // Set local state for editing
    setEditingClass({
      ...classItem,
      schedules,
      startDate: startDate,
      endDate: classItem.endDate ? classItem.endDate.toDate() : null,
      timezone: timezone, // Set timezone with user's local timezone as fallback
      paymentConfig: {
        ...classItem.paymentConfig,
        monthlyOption: monthlyOption
      }
    });
  };

  // Function to handle saving all changes
  const handleSaveChanges = async () => {
    if (!editingClass) return;
    setIsSaving(true);

    try {
      let updatedContractUrl = editingClass.contractUrl;

      // Handle contract file upload if provided
      if (editContractFile) {
        const cleanFileName = editContractFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const timestamp = Date.now();
        const finalFileName = `${timestamp}_${cleanFileName}`;
        const storageRef = ref(storage, `contracts/${editingClass.id}/${finalFileName}`);
        
        // Upload with metadata
        const metadata = {
          contentType: editContractFile.type,
          customMetadata: {
            originalName: editContractFile.name,
            uploadedAt: new Date().toISOString()
          }
        };
        
        logQuery('Uploading updated contract file', { filename: finalFileName, metadata });
        await uploadBytes(storageRef, editContractFile, metadata);
        logQuery('Getting updated contract file download URL', { filename: finalFileName });
        updatedContractUrl = await getDownloadURL(storageRef);
      }

      // Find the original class to check for versioned config changes
      const originalClass = classes.find(c => c.id === editingClass.id);
      const scheduleChanged =
        !!originalClass && hasScheduleChanged(originalClass, editingClass);
      const paymentChanged =
        !!originalClass && hasPaymentConfigChanged(originalClass, editingClass);
      const configChanged = scheduleChanged || paymentChanged;

      if (configChanged && originalClass) {
        logQuery('Config changed, archiving prior config to versions subcollection', {
          classId: editingClass.id,
          scheduleChanged,
          paymentChanged,
        });
        await archiveClassConfigVersion(editingClass.id, originalClass);
      }

      const configEffectiveFrom = configChanged
        ? Timestamp.now()
        : (editingClass.configEffectiveFrom ||
            originalClass?.configEffectiveFrom ||
            originalClass?.startDate ||
            originalClass?.createdAt ||
            Timestamp.now());

      const updatedClass = {
        ...editingClass,
        contractUrl: updatedContractUrl,
        startDate: Timestamp.fromDate(editingClass.startDate instanceof Date ? editingClass.startDate : editingClass.startDate.toDate()),
        endDate: editingClass.endDate ? Timestamp.fromDate(editingClass.endDate instanceof Date ? editingClass.endDate : editingClass.endDate.toDate()) : null,
        updatedAt: Timestamp.now(),
        configEffectiveFrom,
      };

      // Strip legacy embedded scheduleHistory if present
      const firestoreUpdate = {
        ...updatedClass,
        scheduleHistory: deleteField(),
      };

      logQuery('Updating class', { classId: editingClass.id, updates: updatedClass });
      await updateCachedDocument('classes', editingClass.id, firestoreUpdate);
      logQuery('Invalidating calendar cache');
      await invalidateCalendarCache();

      // Update local state (omit FieldValue sentinels)
      setClasses(prevClasses => 
        prevClasses.map(c => {
          if (c.id !== editingClass.id) return c;
          const { scheduleHistory: _removed, ...rest } = { ...c, ...updatedClass } as Class & {
            scheduleHistory?: unknown;
          };
          return rest as Class;
        })
      );

      setEditingClass(null);
      setEditContractFile(null);
      setIsModalOpen(false);
      toast.success(t.updateSuccessful);
    } catch (error) {
      logQuery('Error updating class', error);
      toast.error(t.updateFailed);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle contract file change
  const handleContractFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Check file size (5MB limit)
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 5) {
        toast.error('Contract file size must be less than 5MB');
        return;
      }
      
      // Check file type (PDF only)
      if (file.type !== 'application/pdf') {
        toast.error('Contract file must be a PDF');
        return;
      }
      
      setContractFile(file);
    }
  };
  
  // Handle edit contract file change
  const handleEditContractFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Check file size (5MB limit)
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 5) {
        toast.error('Contract file size must be less than 5MB');
        return;
      }
      
      // Check file type (PDF only)
      if (file.type !== 'application/pdf') {
        toast.error('Contract file must be a PDF');
        return;
      }
      
      setEditContractFile(file);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentStep('schedule');
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  const renderMobileCard = (classItem: Class) => (
    <div key={classItem.id} className={styles.card.container}>
      <div className="space-y-2">
        <div className="flex justify-between items-start">
          <div className="text-gray-900">
            <div className={styles.card.title}>
              {classItem.scheduleType === 'multiple' ? 'Multiple Days Schedule' : DAYS_OF_WEEK[classItem.dayOfWeek]}
            </div>
            {classItem.scheduleType === 'single' ? (
            <div className={styles.card.subtitle}>{classItem.startTime} - {classItem.endTime}</div>
            ) : (
              <div className="mt-2">
                <div className={styles.card.label}>Schedule</div>
                <div className="text-gray-800">
                  {classItem.schedules && classItem.schedules.length > 0 ? (
                    <div className="space-y-1">
                      {classItem.schedules.map((schedule: ClassSchedule, index: number) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium">{DAYS_OF_WEEK[schedule.dayOfWeek]}:</span>{' '}
                          {formatTimeWithTimezones(
                            schedule.startTime,
                            schedule.endTime,
                            schedule.timezone || classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                            classItem.startDate?.toDate()
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">No schedule details available</div>
                  )}
                </div>
              </div>
            )}
            <div className="mt-2">
              <div className={styles.card.label}>Frequency</div>
              <div className="text-gray-800">
                {`Every ${classItem.frequency?.every || 1} ${classItem.frequency?.every === 1 ? 'week' : 'weeks'}`}
              </div>
            </div>
            <div className="mt-2">
              <div className={styles.card.label}>{t.courseType}</div>
              <div className="text-gray-800">{classItem.courseType}</div>
            </div>
            <div className="mt-2">
              <div className={styles.card.label}>{t.students}</div>
              <div className="max-h-24 overflow-y-auto">
                {classItem.studentEmails.map((email: string) => {
                  const student = users.find(u => u.email === email);
                  return student ? (
                    <div key={`${classItem.id}-${email}`} className="mb-1">
                      {student.name}{student.status === 'pending' ? ` (${t.pending})` : ''}
                    </div>
                  ) : (
                    <div key={`${classItem.id}-${email}`} className="mb-1 text-red-500">
                      {t.unknownEmail}: {email}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-2">
              <div className={styles.card.label}>Payment Details</div>
              <div className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Payment:</span> {classItem.paymentConfig?.type === 'weekly'
                  ? ((classItem.paymentConfig.weeklyInterval || 1) === 1
                    ? 'Weekly'
                    : `Every ${classItem.paymentConfig.weeklyInterval} weeks`)
                  : classItem.paymentConfig?.monthlyOption === 'first'
                    ? 'Monthly (1st day)'
                    : classItem.paymentConfig?.monthlyOption === 'fifteen'
                      ? 'Monthly (15th day)'
                      : 'Monthly (last day)'
                  }
                {classItem.paymentConfig?.paymentLink && (
                  <div className="mt-1">
                    <a 
                      href={classItem.paymentConfig.paymentLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Payment Link
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2">
              <div className={styles.card.label}>Payment Amount</div>
              <div className="text-gray-800">
                {classItem.paymentConfig?.currency || 'USD'} {classItem.paymentConfig?.amount?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="mt-2">
              <div className={styles.card.label}>Payment Day</div>
              <div className="text-gray-800">
                {classItem.paymentConfig?.type === 'weekly'
                  ? (() => {
                      const [year, month, day] = classItem.paymentConfig.startDate.split('-').map(Number);
                      const date = new Date(year, month - 1, day);
                      return getDayName(date.getDay(), t);
                    })()
                  : classItem.paymentConfig?.monthlyOption === 'first'
                    ? '1st'
                    : classItem.paymentConfig?.monthlyOption === 'fifteen'
                      ? '15th'
                      : 'Last'
                  }
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => openEditModal(classItem)}
              className="p-2 text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
              title={t.edit}
            >
              <PencilIcon className="h-5 w-5" />
              <span className="sr-only">{t.edit}</span>
            </button>
            <button
              onClick={() => confirmDeleteClass(classItem.id)}
              disabled={deletingClassId === classItem.id}
              className={`p-2 ${deletingClassId === classItem.id ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'} transition-colors duration-200`}
              title={t.delete}
            >
              {deletingClassId === classItem.id ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="sr-only">Deleting...</span>
                </>
              ) : (
                <>
                  <TrashIcon className="h-5 w-5" />
                  <span className="sr-only">{t.delete}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Add these functions to handle schedule management for editing
  const handleAddScheduleToEdit = () => {
    if (!editingClass) return;
    
    // Get the time options for 9:00 AM and 10:00 AM
    const defaultStartTime = timeOptions.find(time => time.includes('9:00') && time.includes('AM')) || '09:00 AM';
    const defaultEndTime = timeOptions.find(time => time.includes('10:00') && time.includes('AM')) || '10:00 AM';
    
    setEditingClass((prev: any) => {
      // Default to Sunday (0) if no schedules exist yet, otherwise use the next day after the last added day
      const lastDayOfWeek = prev.schedules.length > 0 
        ? prev.schedules[prev.schedules.length - 1].dayOfWeek 
        : -1;
      
      // Choose the next day that isn't already in the schedules
      let newDayOfWeek = (lastDayOfWeek + 1) % 7;
      const existingDays = prev.schedules.map((s: ClassSchedule) => s.dayOfWeek);
      
      // Find the first day that isn't already scheduled
      while (existingDays.includes(newDayOfWeek)) {
        newDayOfWeek = (newDayOfWeek + 1) % 7;
      }
      
      const newSchedule = { 
        dayOfWeek: newDayOfWeek, 
        startTime: defaultStartTime, 
        endTime: defaultEndTime,
        timezone: prev.timezone // Use the class timezone without fallback
      };
      
      const updatedSchedules = [...prev.schedules, newSchedule];
      
      // Check if the current start date's day of week is in the selected days
      const currentStartDate = prev.startDate instanceof Timestamp 
        ? prev.startDate.toDate() 
        : prev.startDate;
      const currentStartDateDay = currentStartDate.getDay();
      const selectedDays = updatedSchedules.map(schedule => schedule.dayOfWeek);
      const isCurrentStartDateValid = selectedDays.includes(currentStartDateDay);
      
      // If this is the first schedule or the current start date is not valid,
      // update the start date to the next occurrence of the new day
      if (prev.schedules.length === 0 || !isCurrentStartDateValid) {
        const nextOccurrence = getNextDayOccurrence(newDayOfWeek);
        
        return {
          ...prev,
          schedules: updatedSchedules,
          startDate: nextOccurrence,
          // Reset end date if it's now before the start date
          ...(prev.endDate && 
             (prev.endDate instanceof Timestamp 
              ? prev.endDate.toDate() < nextOccurrence 
              : prev.endDate < nextOccurrence) 
             ? { endDate: null } : {})
        };
      }
      
      return {
        ...prev,
        schedules: updatedSchedules
      };
    });
  };

  const handleRemoveScheduleFromEdit = (index: number) => {
    if (!editingClass) return;
    
    setEditingClass((prev: any) => {
      // Get the schedule that's being removed
      const scheduleToRemove = prev.schedules[index];
      
      // Filter out the schedule at the specified index
      const updatedSchedules = prev.schedules.filter((_: any, i: number) => i !== index);
      
      // If we're removing the last schedule, return to single day mode
      if (updatedSchedules.length === 0) {
        return {
          ...prev,
          scheduleType: 'single',
          schedules: []
        };
      }
      
      // Check if the current start date's day of week matches the day being removed
      const currentStartDate = prev.startDate instanceof Timestamp 
        ? prev.startDate.toDate() 
        : prev.startDate;
      const currentStartDateDay = currentStartDate.getDay();
      const isRemovingCurrentDay = scheduleToRemove.dayOfWeek === currentStartDateDay;
      
      // Check if the day being removed is the only instance of that day in the schedules
      const remainingDaysOfWeek = updatedSchedules.map((s: ClassSchedule) => s.dayOfWeek);
      const dayStillExists = remainingDaysOfWeek.includes(currentStartDateDay);
      
      // If we're removing the day that matches the current start date and it doesn't exist in other schedules
      if (isRemovingCurrentDay && !dayStillExists && updatedSchedules.length > 0) {
        // Update the start date to the next occurrence of the first remaining day
        const nextOccurrence = getNextDayOccurrence(updatedSchedules[0].dayOfWeek);
        
        return {
          ...prev,
          schedules: updatedSchedules,
          startDate: nextOccurrence,
          // Reset end date if it's now before the start date
          ...(prev.endDate && 
             (prev.endDate instanceof Timestamp 
              ? prev.endDate.toDate() < nextOccurrence 
              : prev.endDate < nextOccurrence) 
             ? { endDate: null } : {})
        };
      }
      
      return {
        ...prev,
        schedules: updatedSchedules
      };
    });
  };

  const handleEditScheduleChange = (index: number, field: keyof ClassSchedule, value: string | number) => {
    if (!editingClass) return;
    
    setEditingClass((prev: any) => {
      const updatedSchedules = [...prev.schedules];
      updatedSchedules[index] = {
        ...updatedSchedules[index],
        [field]: value,
        // Always ensure timezone is set when making any schedule changes
        timezone: updatedSchedules[index].timezone || prev.timezone
      };
      
      // If changing day of week, update start date if needed
      if (field === 'dayOfWeek') {
        // When day of week is changed, we need to update the start date if it's currently
        // set to a day that doesn't match any of the schedules
        const newDayOfWeek = value as number;
        
        // Get all selected days of week from schedules (including the newly updated one)
        const selectedDays = updatedSchedules.map(schedule => schedule.dayOfWeek);
        
        // Check if the current start date's day of week is in the selected days
        const currentStartDate = prev.startDate instanceof Timestamp 
          ? prev.startDate.toDate() 
          : prev.startDate;
        const currentStartDateDay = currentStartDate.getDay();
        const isCurrentStartDateValid = selectedDays.includes(currentStartDateDay);
        
        // If the current start date is not valid anymore, update it to the next occurrence of the new day
        if (!isCurrentStartDateValid) {
          const nextOccurrence = getNextDayOccurrence(newDayOfWeek);
          
          return {
            ...prev,
            schedules: updatedSchedules,
            startDate: nextOccurrence,
            // Reset end date if it's now before the start date
            ...(prev.endDate && 
               (prev.endDate instanceof Timestamp 
                ? prev.endDate.toDate() < nextOccurrence 
                : prev.endDate < nextOccurrence) 
               ? { endDate: null } : {})
          };
        }
      } else if (field === 'startTime') {
        // Parse the selected start time
        const [startTimeStr, startPeriod] = value.toString().split(' ');
        const [startHours, startMinutes] = startTimeStr.split(':').map(Number);
        
        // Create Date objects for start and end times
        const startDate = new Date();
        startDate.setHours(startPeriod === 'PM' && startHours !== 12 ? startHours + 12 : (startPeriod === 'AM' && startHours === 12 ? 0 : startHours), startMinutes);
        
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
        
        // Format end time in 12-hour format
        const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        
        updatedSchedules[index].endTime = endTime;
      } else if (field === 'endTime') {
        // Parse both times
        const [startTimeStr, startPeriod] = updatedSchedules[index].startTime.split(' ');
        const [endTimeStr, endPeriod] = value.toString().split(' ');
        const [startHours, startMinutes] = startTimeStr.split(':').map(Number);
        const [endHours, endMinutes] = endTimeStr.split(':').map(Number);

        // Create Date objects for comparison
        const startDate = new Date();
        const endDate = new Date();
        
        startDate.setHours(
          startPeriod === 'PM' && startHours !== 12 ? startHours + 12 : (startPeriod === 'AM' && startHours === 12 ? 0 : startHours),
          startMinutes
        );
        endDate.setHours(
          endPeriod === 'PM' && endHours !== 12 ? endHours + 12 : (endPeriod === 'AM' && endHours === 12 ? 0 : endHours),
          endMinutes
        );

        // If end time is before start time, adjust start time to be 1 hour before end time
        if (endDate <= startDate) {
          const newStartDate = new Date(endDate.getTime() - 60 * 60 * 1000);
          const newStartTime = newStartDate.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: true 
          });
          
          updatedSchedules[index].startTime = newStartTime;
        }
      }
      
      return {
        ...prev,
        schedules: updatedSchedules
      };
    });
  };

  // Add sorting function before the return statement
  const handleSort = (column: 'dayTime' | 'students' | 'amount') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedClasses = () => {
    return [...classes].sort((a, b) => {
      let comparison = 0;

      if (sortColumn === 'dayTime') {
        // Sort by day and time
        if (a.scheduleType === 'multiple' && b.scheduleType === 'multiple') {
          // For multiple schedules, sort by the first schedule's day and time
          const aSchedule = a.schedules[0];
          const bSchedule = b.schedules[0];
          if (!aSchedule || !bSchedule) return 0;
          
          if (aSchedule.dayOfWeek !== bSchedule.dayOfWeek) {
            comparison = aSchedule.dayOfWeek - bSchedule.dayOfWeek;
          } else {
            comparison = aSchedule.startTime.localeCompare(bSchedule.startTime);
          }
        } else if (a.scheduleType === 'multiple') {
          // Multiple schedule classes come after single schedule
          comparison = 1;
        } else if (b.scheduleType === 'multiple') {
          // Single schedule classes come before multiple schedule
          comparison = -1;
        } else {
          // Both are single schedule
          if (a.dayOfWeek !== b.dayOfWeek) {
            comparison = a.dayOfWeek - b.dayOfWeek;
          } else {
            comparison = a.startTime.localeCompare(b.startTime);
          }
        }
      } else if (sortColumn === 'students') {
        // Sort by first student's name alphabetically
        const aFirstEmail = a.studentEmails[0] || '';
        const bFirstEmail = b.studentEmails[0] || '';
        const aStudent = users.find(u => u.email === aFirstEmail);
        const bStudent = users.find(u => u.email === bFirstEmail);
        const aName = aStudent?.name.toLowerCase() || aFirstEmail.toLowerCase();
        const bName = bStudent?.name.toLowerCase() || bFirstEmail.toLowerCase();
        comparison = aName.localeCompare(bName);
      } else if (sortColumn === 'amount') {
        // Sort by payment amount numerically
        const aAmount = a.paymentConfig?.amount || 0;
        const bAmount = b.paymentConfig?.amount || 0;
        comparison = aAmount - bAmount;
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  return (
    <div className="flex-1">
      <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className={styles.headings.h1}>{t.manageClasses}</h1>
          <div className="relative">
            <button
              onClick={async () => {
                if (!isModalOpen) {
                  await fetchAllUsers();
                  const today = new Date();
                  
                  // Default start time (9:00 AM)
                  const defaultStartTime = timeOptions.find(time => time.includes('9:00') && time.includes('AM')) || '09:00 AM';
                  // Default end time (10:00 AM)
                  const defaultEndTime = timeOptions.find(time => time.includes('10:00') && time.includes('AM')) || '10:00 AM';
                  
                  // Initialize with one day (Monday) already added
                  const initialSchedule = {
                    dayOfWeek: 1, // Monday
                    startTime: defaultStartTime,
                    endTime: defaultEndTime
                  };
                  
                  setNewClass({
                    scheduleType: 'multiple',
                    dayOfWeek: 1,
                    startTime: defaultStartTime,
                    endTime: defaultEndTime,
                    schedules: [initialSchedule], // Start with one schedule
                    courseType: 'Individual',
                    studentEmails: [],
                    startDate: getNextDayOccurrence(1), // Start date is next Monday
                    endDate: null,
                    contractUrl: '',
                    paymentConfig: {
                      type: 'weekly',
                      weeklyInterval: 1,
                      monthlyOption: null,
                      startDate: today.toISOString().split('T')[0],
                      paymentLink: '',
                      amount: 0,
                      currency: 'USD'
                    },
                    frequency: {
                      type: 'custom',
                      every: 1
                    }
                  });
                }
                setIsModalOpen(!isModalOpen);
              }}
              className={classNames(
                isModalOpen 
                  ? "bg-gray-200 hover:bg-gray-300 text-gray-800" 
                  : styles.buttons.primary,
                "focus:outline-none focus:ring-2 focus:ring-offset-2",
                isModalOpen ? "focus:ring-gray-500" : "focus:ring-indigo-500"
              )}
            >
              {isModalOpen ? (
                <span className="text-xl">&times;</span>
              ) : (
                t.addNewClass
              )}
            </button>
          </div>
        </div>

        {/* Add Class Form */}
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <div className="max-w-4xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {currentStep === 'schedule' ? 'Add New Class - Schedule' : 'Add New Class - Payment'}
              </h2>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (currentStep === 'schedule') {
                setCurrentStep('payment');
              } else {
                handleCreateClass(e);
              }
            }}>
              {currentStep === 'schedule' ? (
                /* Class Configuration Section */
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
                    {"Class Configuration"}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={styles.form.label}>
                        {t.students}
                      </label>
                      <Select
                        isMulti
                        value={studentOptions.filter(option => 
                          newClass.studentEmails.includes(option.value)
                        )}
                        onChange={handleStudentChange}
                        options={studentOptions}
                        className="mt-1"
                        classNamePrefix="select"
                        styles={customSelectStyles}
                      />
                    </div>
                    
                    <div className="md:col-span-2 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className={styles.form.label}>Class Schedules</label>
                        <button
                          type="button"
                          onClick={handleAddSchedule}
                          className="py-1 px-2 text-sm inline-flex items-center rounded border border-indigo-600 bg-white text-indigo-600 hover:bg-indigo-50"
                        >
                          <PlusIcon className="h-3.5 w-3.5 mr-1" /> Add Day
                        </button>
                      </div>
                      
                      {newClass.schedules.length === 0 ? (
                        <div className="p-4 border border-gray-200 rounded-md bg-gray-50 text-center">
                          Please add at least one day to the class schedule
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {newClass.schedules.map((schedule: ClassSchedule, index: number) => (
                            <div key={index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                              <div className="flex justify-between items-center mb-3">
                                <h4 className="font-medium">Class #{index + 1}</h4>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSchedule(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Day</label>
                                  <select
                                    value={schedule.dayOfWeek}
                                    onChange={(e) => handleScheduleChange(index, 'dayOfWeek', parseInt(e.target.value))}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  >
                                    {DAYS_OF_WEEK.map((day, dayIndex) => (
                                      <option key={day} value={dayIndex}>{day}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Start Time</label>
                                  <select
                                    value={schedule.startTime}
                                    onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  >
                                    {timeOptions.map(time => (
                                      <option key={time} value={time}>{time}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">End Time</label>
                                  <select
                                    value={schedule.endTime}
                                    onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  >
                                    {timeOptions.map(time => (
                                      <option key={time} value={time}>{time}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Add frequency selection here */}
                      <div>
                        <label className={styles.form.label}>
                          Class Frequency
                          <Tooltip text="How often the class repeats. For example, select 2 for classes that occur every 2 weeks.">
                            <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 ml-1 inline-block" />
                          </Tooltip>
                        </label>
                        <div className="flex items-center">
                          <span className="text-gray-800 mr-2">Every</span>
                          <input
                            type="number"
                            min="1"
                            value={newClass.frequency.every}
                            onChange={(e) => {
                              const every = parseInt(e.target.value) || 1;
                              setNewClass((prev: typeof newClass) => ({
                                ...prev,
                                frequency: {
                                  type: 'custom',
                                  every: Math.max(1, every)
                                }
                              }));
                            }}
                            className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                          <span className="text-gray-800 ml-2">weeks</span>
                        </div>
                      </div>
                      
                      {/* Add timezone selection */}
                      <div>
                        <label className={styles.form.label}>Timezone <span className="text-red-500">*</span></label>
                        <select
                          value={newClass.timezone}
                          onChange={(e) => {
                            setNewClass((prev: typeof newClass) => ({
                              ...prev,
                              timezone: e.target.value,
                              // Also update timezone in all schedules
                              schedules: prev.schedules.map((s: ClassSchedule) => ({
                                ...s,
                                timezone: e.target.value
                              }))
                            }));
                          }}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          {getTimezoneOptions().map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Class times will be displayed in this timezone. Students will see times converted to their local timezone.
                        </p>
                      </div>
                    </div>
                    
                    <div className="md:col-span-2 flex space-x-4">
                      <div className="flex-1">
                        <label className={styles.form.label}>{t.classStartDate || "Class Start Date"}</label>
                        <DatePicker
                          selected={newClass.startDate || new Date()}
                          onChange={(date: Date | null) => {
                            if (date) {
                              setNewClass((prev: typeof newClass) => ({
                                ...prev,
                                startDate: date,
                                // Reset end date if it's now before the start date
                                ...(prev.endDate && prev.endDate < date ? { endDate: null } : {})
                              }));
                            }
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          showTimeSelect={false}
                          dateFormat="MMMM d, yyyy"
                          minDate={new Date()}
                          filterDate={(date) => {
                            // Only allow selecting dates that match any of the selected days
                            if (newClass.schedules.length > 0) {
                              const selectedDays = newClass.schedules.map((schedule: ClassSchedule) => schedule.dayOfWeek);
                              return selectedDays.includes(date.getDay());
                            }
                            
                            return true; // Allow all dates if no schedules defined yet
                          }}
                        />
                        {newClass.schedules.length > 0 && (
                          <p className="mt-1 text-xs text-gray-500">
                            Only {newClass.schedules.map((s: ClassSchedule) => DAYS_OF_WEEK[s.dayOfWeek]).join(', ')} dates can be selected
                          </p>
                        )}
                      </div>
                      <div className="flex-1">
                        <label className={styles.form.label}>{t.endDate || "Class End Date"} <span className="text-gray-500 text-xs">({t.optional})</span></label>
                        <DatePicker
                          selected={newClass.endDate}
                          onChange={(date: Date | null) => {
                            setNewClass((prev: typeof newClass) => ({
                              ...prev,
                              endDate: date
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          showTimeSelect={false}
                          dateFormat="MMMM d, yyyy"
                          minDate={newClass.startDate}
                          isClearable={true}
                          placeholderText={t.noEndDate || "No end date"}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className={styles.form.label}>
                        Contract (PDF, max 5MB)
                        <span className="ml-1 text-gray-500 text-xs">(optional)</span>
                        <Tooltip text="Upload a PDF contract document for this class. This is optional but recommended for keeping track of agreements with students.">
                          <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 ml-1 inline-block" />
                        </Tooltip>
                      </label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleContractFileChange}
                        className="mt-1 block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-indigo-50 file:text-indigo-700
                          hover:file:bg-indigo-100"
                      />
                      {contractFile && (
                        <div className="mt-2 text-sm text-gray-700 flex items-center gap-2">
                          <span>Selected file: {contractFile.name}</span>
                          <button
                            type="button"
                            onClick={() => setContractFile(null)}
                            className="p-0.5 bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                            aria-label="Remove file"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* Payment Configuration Section */
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
                    {"Payment Configuration"}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={styles.form.label}>{"Payment Type"}</label>
                      <select
                        value={newClass.paymentConfig.type}
                        onChange={(e) => {
                          const type = e.target.value as 'weekly' | 'monthly';
                          setNewClass((prev: typeof newClass) => {
                            if (type === 'monthly') {
                              // If switching to monthly, adjust the start date to a valid day (1, 15, or last day)
                              const currentDate = (() => {
                                const [year, month, day] = prev.paymentConfig.startDate.split('-').map(Number);
                                const date = new Date();
                                date.setFullYear(year);
                                date.setMonth(month - 1); // Month is 0-indexed in JavaScript
                                date.setDate(day);
                                return date;
                              })();
                              
                              const day = currentDate.getDate();
                              const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                              
                              // Determine the closest valid day (1, 15, or last day)
                              let newDay: number;
                              if (day <= 8) {
                                newDay = 1;
                              } else if (day <= 23) {
                                newDay = 15;
                              } else {
                                newDay = lastDayOfMonth;
                              }
                              
                              // Determine the corresponding monthlyOption
                              let monthlyOption: 'first' | 'fifteen' | 'last';
                              if (newDay === 1) {
                                monthlyOption = 'first';
                              } else if (newDay === 15) {
                                monthlyOption = 'fifteen';
                              } else {
                                monthlyOption = 'last';
                              }
                              
                              // Create new date string with adjusted day
                              const year = currentDate.getFullYear();
                              const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                              const adjustedDateStr = `${year}-${month}-${String(newDay).padStart(2, '0')}`;
                              
                              return {
                                ...prev,
                                paymentConfig: {
                                  ...prev.paymentConfig,
                                  type,
                                  startDate: adjustedDateStr,
                                  monthlyOption: monthlyOption,
                                  weeklyInterval: null
                                }
                              };
                            }
                            
                            return {
                              ...prev,
                              paymentConfig: {
                                ...prev.paymentConfig,
                                type,
                                startDate: prev.paymentConfig.startDate,
                                ...(type === 'weekly' 
                                  ? { weeklyInterval: 1, monthlyOption: null } 
                                  : { monthlyOption: 'first', weeklyInterval: null })
                              }
                            };
                          });
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="weekly">Every X number of weeks</option>
                        <option value="monthly">Once a month</option>
                      </select>
                    </div>
                    <div>
                      <label className={styles.form.label}>{t.paymentStartDate || "Payment Start Date"}</label>
                      <DatePicker
                        selected={(() => {
                          // Fix: Parse the date string correctly to avoid timezone issues
                          const [year, month, day] = newClass.paymentConfig.startDate.split('-').map(Number);
                          const date = new Date();
                          date.setFullYear(year);
                          date.setMonth(month - 1); // Month is 0-indexed in JavaScript
                          date.setDate(day);
                          date.setHours(12, 0, 0, 0); // Set to noon to avoid any timezone issues
                          return date;
                        })()}
                        onChange={(date: Date | null) => {
                          if (date) {
                            // Validate date for monthly payment type
                            if (newClass.paymentConfig.type === 'monthly') {
                              const day = date.getDate();
                              const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                              
                              // Only allow 1st, 15th, or last day of month for monthly payments
                              if (day !== 1 && day !== 15 && day !== lastDayOfMonth) {
                                toast.error("For monthly payments, start date must be the 1st, 15th, or last day of the month");
                                return;
                              }
                              
                              // Automatically set the monthlyOption based on the selected date
                              let monthlyOption: 'first' | 'fifteen' | 'last';
                              if (day === 1) {
                                monthlyOption = 'first';
                              } else if (day === 15) {
                                monthlyOption = 'fifteen';
                              } else {
                                monthlyOption = 'last';
                              }
                              
                              // Fix: Use local date string to prevent timezone issues
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const dateStr = `${year}-${month}-${String(day).padStart(2, '0')}`;
                              
                              setNewClass((prev: typeof newClass) => ({
                                ...prev,
                                paymentConfig: {
                                  ...prev.paymentConfig,
                                  startDate: dateStr,
                                  monthlyOption: monthlyOption
                                }
                              }));
                              return;
                            }
                            
                            // For weekly payments, just update the date
                            // Fix: Use local date string to prevent timezone issues
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            const dateStr = `${year}-${month}-${day}`;
                            
                            setNewClass((prev: typeof newClass) => ({
                              ...prev,
                              paymentConfig: {
                                ...prev.paymentConfig,
                                startDate: dateStr
                              }
                            }));
                          }
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        showTimeSelect={false}
                        dateFormat="MMMM d, yyyy"
                        filterDate={(date) => {
                          // For monthly payments, only allow selecting 1st, 15th, or last day of month
                          if (newClass.paymentConfig.type === 'monthly') {
                            const day = date.getDate();
                            const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                            return day === 1 || day === 15 || day === lastDayOfMonth;
                          }
                          return true; // No filter for weekly payments
                        }}
                      />
                    </div>
                    {newClass.paymentConfig.type === 'weekly' ? (
                      <div>
                        <label htmlFor="weeklyInterval" className={styles.form.label}>
                          {"Payment Frequency"}
                          <Tooltip text="How often payments should be collected. This is only applicable for weekly classes.">
                            <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 ml-1 inline-block" />
                          </Tooltip>
                        </label>
                        <div className="flex items-center">
                          <span className="mr-2">Every</span>
                          <input
                            type="number"
                            id="weeklyInterval"
                            min="1"
                            value={newClass.paymentConfig.weeklyInterval === undefined ? '' : newClass.paymentConfig.weeklyInterval}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewClass((prev: typeof newClass) => ({
                                ...prev,
                                paymentConfig: {
                                  ...prev.paymentConfig,
                                  weeklyInterval: value === '' ? undefined : Math.max(1, parseInt(value))
                                }
                              }));
                            }}
                            className="mt-1 w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                          <span className="ml-2">{newClass.paymentConfig.weeklyInterval === 1 ? 'week' : 'weeks'}</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label htmlFor="monthlyOption" className={styles.form.label}>
                          {t.selectPaymentDay || "Payment Day"}
                          <span className="ml-1 text-gray-500 text-xs">
                            (auto-set)
                          </span>
                        </label>
                        <div className="relative">
                          <div className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 text-gray-500 px-3 py-2 sm:text-sm flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {newClass.paymentConfig.monthlyOption === 'first' && t.firstDayMonth}
                            {newClass.paymentConfig.monthlyOption === 'fifteen' && t.fifteenthDayMonth}
                            {newClass.paymentConfig.monthlyOption === 'last' && t.lastDayMonth}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Payment day is automatically set based on the selected payment start date.
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label htmlFor="paymentLink" className={styles.form.label}>
                        Payment Link
                      </label>
                      <input
                        type="url"
                        id="paymentLink"
                        value={newClass.paymentConfig.paymentLink || ''}
                        onChange={(e) => setNewClass((prev: typeof newClass) => ({
                          ...prev,
                          paymentConfig: {
                            ...prev.paymentConfig,
                            paymentLink: e.target.value
                          }
                        }))}
                        placeholder="https://payment-provider.com/your-payment-link"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter a URL where students can make payments
                      </p>
                    </div>
                    
                    <div>
                      <label htmlFor="paymentAmount" className={styles.form.label}>
                        Payment Amount
                      </label>
                      <input
                        type="number"
                        id="paymentAmount"
                        min="0"
                      step="0.01"
                      value={newClass.paymentConfig.amount === undefined ? '' : newClass.paymentConfig.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewClass((prev: typeof newClass) => ({
                          ...prev,
                          paymentConfig: {
                            ...prev.paymentConfig,
                            amount: value === '' ? undefined : parseFloat(value)
                          }
                        }));
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    </div>
                    
                    <div>
                      <label htmlFor="paymentCurrency" className={styles.form.label}>
                        Currency
                      </label>
                      <select
                        id="paymentCurrency"
                        value={newClass.paymentConfig.currency || 'USD'}
                        onChange={(e) => setNewClass((prev: typeof newClass) => ({
                          ...prev,
                          paymentConfig: {
                            ...prev.paymentConfig,
                            currency: e.target.value
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="BRL">BRL (R$)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                {currentStep === 'payment' && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep('schedule')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {currentStep === 'schedule' ? 'Next' : 'Create Class'}
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-red-100 rounded-full p-2">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t.manageClasses}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {t.admin.schedule.confirmDelete}
                  </p>
                  <div className="mt-4 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={cancelDeleteClass}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {t.cancel}
                    </button>
                    <button
                      type="button"
                      onClick={() => classToDelete && handleDeleteClass(classToDelete)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      {deletingClassId ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t.loading}
                        </>
                      ) : t.delete}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showMobileView ? (
          <div className="space-y-4">
            {getSortedClasses().map(renderMobileCard)}
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden relative">
            <div className="hidden md:flex justify-end mb-2">
              <div className="text-sm text-gray-500 flex items-center pr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Scroll horizontally to see all columns
              </div>
            </div>
            <div className="relative">
              {/* Left fade indicator */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none hidden md:block"></div>
              
              <div className="table-container overflow-x-auto overflow-y-auto max-h-[600px] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-500">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th scope="col" className={`${styles.table.header} w-20`}>
                        {/* Empty header for actions */}
                      </th>
                      <th 
                        scope="col" 
                        className={`${styles.table.header} cursor-pointer hover:bg-gray-100 select-none`}
                        onClick={() => handleSort('dayTime')}
                      >
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <span>{t.dayAndTime}</span>
                          {sortColumn === 'dayTime' ? (
                            sortDirection === 'asc' ? (
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )
                          ) : (
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th scope="col" className={styles.table.header}>
                        Frequency
                      </th>
                      <th 
                        scope="col" 
                        className={`${styles.table.header} cursor-pointer hover:bg-gray-100 select-none`}
                        onClick={() => handleSort('students')}
                      >
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <span>{t.students}</span>
                          {sortColumn === 'students' ? (
                            sortDirection === 'asc' ? (
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )
                          ) : (
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th scope="col" className={styles.table.header}>
                        Payment Type
                      </th>
                      <th 
                        scope="col" 
                        className={`${styles.table.header} cursor-pointer hover:bg-gray-100 select-none`}
                        onClick={() => handleSort('amount')}
                      >
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <span>Payment Amount</span>
                          {sortColumn === 'amount' ? (
                            sortDirection === 'asc' ? (
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )
                          ) : (
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th scope="col" className={styles.table.header}>
                        Payment Day
                      </th>
                      <th scope="col" className={styles.table.header}>
                        Contract
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedClasses().map((classItem) => (
                      <tr key={classItem.id} className="group hover:bg-gray-50">
                        <td className={`${styles.table.cell} w-20`}>
                          <div className="flex items-center justify-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="relative">
                              <button
                                onClick={() => openEditModal(classItem)}
                                className="p-1.5 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              >
                                <PencilIcon className="h-4 w-4" />
                                <span className="sr-only">{t.edit}</span>
                              </button>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 invisible hover:opacity-100 hover:visible transition-opacity duration-200 whitespace-nowrap">
                                {t.edit}
                              </div>
                            </div>
                            <div className="relative">
                              <button
                                onClick={() => confirmDeleteClass(classItem.id)}
                                disabled={deletingClassId === classItem.id}
                                className={`p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                  deletingClassId === classItem.id 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-red-600 hover:text-red-800 hover:bg-red-50 focus:ring-red-500'
                                }`}
                              >
                                {deletingClassId === classItem.id ? (
                                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <TrashIcon className="h-4 w-4" />
                                )}
                                <span className="sr-only">{deletingClassId === classItem.id ? "Deleting..." : t.delete}</span>
                              </button>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 invisible hover:opacity-100 hover:visible transition-opacity duration-200 whitespace-nowrap">
                                {deletingClassId === classItem.id ? "Deleting..." : t.delete}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={styles.table.cell}>
                          {classItem.scheduleType === 'single' ? (
                            <>
                              {getDayName(classItem.dayOfWeek, t)}<br />
                              {formatTimeWithTimezones(
                                classItem.startTime,
                                classItem.endTime,
                                classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                Intl.DateTimeFormat().resolvedOptions().timeZone,
                                classItem.startDate?.toDate()
                              )}
                            </>
                          ) : (
                            <div className="max-h-32 overflow-y-auto">
                              {classItem.schedules && classItem.schedules.length > 1 && (
                                <div className="font-medium mb-1">Multiple Days:</div>
                              )}
                              {classItem.schedules && classItem.schedules.length > 0 ? (
                                <div className="space-y-1">
                                  {classItem.schedules.map((schedule: ClassSchedule, index: number) => (
                                    <div key={index} className="text-sm">
                                      <span className="font-medium">{DAYS_OF_WEEK[schedule.dayOfWeek]}:</span>{' '}
                                      {formatTimeWithTimezones(
                                        schedule.startTime,
                                        schedule.endTime,
                                        schedule.timezone || classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        classItem.startDate?.toDate()
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-gray-500 italic">No schedule details</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className={styles.table.cell}>
                          {`Every ${classItem.frequency?.every || 1} ${classItem.frequency?.every === 1 ? 'week' : 'weeks'}`}
                        </td>
                        <td className={styles.table.cell}>
                          {classItem.studentEmails.map((email: string) => {
                            const student = users.find(u => u.email === email);
                            return student ? (
                              <div key={`${classItem.id}-${email}`} className="mb-1">
                                {student.name}{student.status === 'pending' ? ` (${t.pending})` : ''}
                              </div>
                            ) : (
                              <div key={`${classItem.id}-${email}`} className="mb-1 text-red-500">
                                {t.unknownEmail}: {email}
                              </div>
                            );
                          })}
                        </td>
                        <td className={styles.table.cell}>
                          {classItem.paymentConfig?.type === 'weekly'
                            ? ((classItem.paymentConfig.weeklyInterval || 1) === 1
                              ? 'Weekly'
                              : `Every ${classItem.paymentConfig.weeklyInterval} weeks`)
                            : classItem.paymentConfig?.monthlyOption === 'first'
                              ? 'Monthly (1st day)'
                              : classItem.paymentConfig?.monthlyOption === 'fifteen'
                                ? 'Monthly (15th day)'
                                : 'Monthly (last day)'
                            }
                          {classItem.paymentConfig?.paymentLink && (
                            <div className="mt-1">
                              <a 
                                href={classItem.paymentConfig.paymentLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                Payment Link
                              </a>
                            </div>
                          )}
                        </td>
                        <td className={`${styles.table.cell} text-center`}>
                          {classItem.paymentConfig?.currency || 'USD'} {classItem.paymentConfig?.amount?.toFixed(2) || '0.00'}
                        </td>
                        <td className={styles.table.cell}>
                          {classItem.paymentConfig?.type === 'weekly'
                            ? (() => {
                                const [year, month, day] = classItem.paymentConfig.startDate.split('-').map(Number);
                                const date = new Date(year, month - 1, day);
                                return getDayName(date.getDay(), t);
                              })()
                            : classItem.paymentConfig?.monthlyOption === 'first'
                              ? '1st day of month'
                              : classItem.paymentConfig?.monthlyOption === 'fifteen'
                                ? '15th day of month'
                                : 'Last day of month'
                            }
                        </td>
                        <td className={styles.table.cell}>
                          {classItem.contractUrl ? (
                            <a 
                              href={classItem.contractUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              View Contract
                            </a>
                          ) : (
                            <span className="text-gray-500">No contract</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        <Modal isOpen={!!editingClass} onClose={() => setEditingClass(null)}>
          <div className="max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className={styles.headings.h2}>{t.edit} {t.class}</h2>
            </div>

            {/* Class Configuration Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
                {"Class Configuration"}
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className={styles.form.label}>
                      {t.students}
                    </label>
                    <Select
                      isMulti
                      value={studentOptions.filter(option => editingClass?.studentEmails.includes(option.value))}
                      onChange={(selected: MultiValue<SelectOption>) => {
                        const selectedEmails = selected ? selected.map(option => option.value) : [];
                        const courseType = selectedEmails.length === 1 ? 'Individual' : 
                                          selectedEmails.length === 2 ? 'Pair' : 'Group';
                        setEditingClass((prev: any) => ({ 
                          ...prev!, 
                          studentEmails: selectedEmails,
                          courseType: courseType
                        }));
                      }}
                      options={studentOptions}
                      className="mt-1"
                      classNamePrefix="select"
                      styles={customSelectStyles}
                    />
                  </div>
                  
                </div>
                
                {/* Schedule Configuration based on type */}
                {editingClass?.scheduleType === 'single' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={styles.form.label}>{t.dayOfWeek}</label>
                      <select
                        value={editingClass?.dayOfWeek}
                        onChange={(e) => {
                          if (!editingClass) return;
                          setEditingClass((prev: any) => ({
                            ...prev,
                            dayOfWeek: parseInt(e.target.value)
                          }));
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        {DAYS_OF_WEEK.map((day, index) => (
                          <option key={day} value={index}>{day}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex space-x-4 md:col-span-2">
                      <div className="flex-1">
                        <label className={styles.form.label}>{"Start Time"}</label>
                        <select
                          value={editingClass?.startTime}
                          onChange={(e) => {
                            if (!editingClass) return;
                            
                            // Parse the selected start time
                            const startTime = e.target.value;
                            const [time, period] = startTime.split(' ');
                            const [hours, minutes] = time.split(':').map(Number);
                            
                            // Create Date objects for start and end times
                            const startDate = new Date();
                            startDate.setHours(period === 'PM' && hours !== 12 ? hours + 12 : (period === 'AM' && hours === 12 ? 0 : hours), minutes);
                            
                            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
                            
                            // Format end time in 12-hour format
                            const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
                            
                            setEditingClass((prev: any) => ({
                              ...prev,
                              startTime,
                              endTime
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          {timeOptions.map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className={styles.form.label}>{"End Time"}</label>
                        <select
                          value={editingClass?.endTime}
                          onChange={(e) => {
                            if (!editingClass) return;
                            
                            // Parse both times
                            const [startTime, startPeriod] = editingClass.startTime.split(' ');
                            const [endTime, endPeriod] = e.target.value.split(' ');
                            const [startHours, startMinutes] = startTime.split(':').map(Number);
                            const [endHours, endMinutes] = endTime.split(':').map(Number);

                            // Create Date objects for comparison
                            const startDate = new Date();
                            const endDate = new Date();
                            
                            startDate.setHours(
                              startPeriod === 'PM' && startHours !== 12 ? startHours + 12 : (startPeriod === 'AM' && startHours === 12 ? 0 : startHours),
                              startMinutes
                            );
                            endDate.setHours(
                              endPeriod === 'PM' && endHours !== 12 ? endHours + 12 : (endPeriod === 'AM' && endHours === 12 ? 0 : endHours),
                              endMinutes
                            );

                            // If end time is before start time, adjust start time to be 1 hour before end time
                            if (endDate <= startDate) {
                              const newStartDate = new Date(endDate.getTime() - 60 * 60 * 1000);
                              const newStartTime = newStartDate.toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit', 
                                hour12: true 
                              });
                              
                              setEditingClass((prev: any) => ({
                                ...prev,
                                startTime: newStartTime,
                                endTime: e.target.value
                              }));
                            } else {
                              setEditingClass((prev: any) => ({
                                ...prev,
                                endTime: e.target.value
                              }));
                            }
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          {timeOptions.map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className={styles.form.label}>Class Schedule</label>
                      <button
                        type="button"
                        onClick={handleAddScheduleToEdit}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Add Day
                      </button>
                    </div>
                    
                    {editingClass?.schedules.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 border border-dashed border-gray-300 rounded-md">
                        No schedules added. Click "Add Day" to add a class day.
                      </div>
                    ) : (
                      <div className="space-y-4 mt-2">
                        {editingClass?.schedules.map((schedule: ClassSchedule, index: number) => (
                          <div key={index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-sm font-medium text-gray-700">Day {index + 1}</h4>
                              <button
                                type="button"
                                onClick={() => handleRemoveScheduleFromEdit(index)}
                                className="text-red-600 hover:text-red-800 bg-transparent"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Day</label>
                                <select
                                  value={schedule.dayOfWeek}
                                  onChange={(e) => handleEditScheduleChange(index, 'dayOfWeek', parseInt(e.target.value))}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                >
                                  {DAYS_OF_WEEK.map((day, dayIndex) => (
                                    <option key={day} value={dayIndex}>{day}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                                <select
                                  value={schedule.startTime}
                                  onChange={(e) => handleEditScheduleChange(index, 'startTime', e.target.value)}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                >
                                  {timeOptions.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">End Time</label>
                                <select
                                  value={schedule.endTime}
                                  onChange={(e) => handleEditScheduleChange(index, 'endTime', e.target.value)}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                >
                                  {timeOptions.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Add frequency selection here */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={styles.form.label}>
                      Class Frequency
                      <Tooltip text="How often the class repeats. For example, select 2 for classes that occur every 2 weeks.">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 ml-1 inline-block" />
                      </Tooltip>
                    </label>
                    <div className="flex items-center">
                      <span className="text-gray-800 mr-2">Every</span>
                      <input
                        type="number"
                        min="1"
                        value={editingClass?.frequency?.every || 1}
                        onChange={(e) => {
                          if (!editingClass) return;
                          const every = parseInt(e.target.value) || 1;
                          setEditingClass((prev: any) => ({
                            ...prev,
                            frequency: {
                              type: 'custom',
                              every: Math.max(1, every)
                            }
                          }));
                        }}
                        className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <span className="text-gray-800 ml-2">weeks</span>
                    </div>
                  </div>
                  
                  {/* Timezone Selection */}
                  <div>
                    <label className={styles.form.label}>Timezone <span className="text-red-500">*</span></label>
                    <select
                      value={editingClass?.timezone}
                      onChange={(e) => {
                        if (!editingClass) return;
                        setEditingClass((prev: any) => ({
                          ...prev,
                          timezone: e.target.value,
                          // Also update timezone in all schedules
                          schedules: prev.schedules.map((s: ClassSchedule) => ({
                            ...s,
                            timezone: e.target.value
                          }))
                        }));
                      }}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      {getTimezoneOptions().map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Date Pickers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={styles.form.label}>{t.classStartDate || "Class Start Date"}</label>
                    <DatePicker
                      selected={editingClass?.startDate || new Date()}
                      onChange={(date: Date | null) => {
                        if (!editingClass || !date) return;
                        setEditingClass((prev: any) => ({
                          ...prev,
                          startDate: date,
                          // Reset end date if it's now before the start date
                          ...(editingClass.endDate && editingClass.endDate < date ? { endDate: null } : {})
                        }));
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      showTimeSelect={false}
                      dateFormat="MMMM d, yyyy"
                      minDate={new Date()}
                      filterDate={(date) => {
                        if (!editingClass) return true;
                        
                        // Only allow selecting dates that match any of the selected days
                        if (editingClass.schedules.length > 0) {
                          const selectedDays = editingClass.schedules.map((schedule: ClassSchedule) => schedule.dayOfWeek);
                          return selectedDays.includes(date.getDay());
                        }
                        
                        return true; // Allow all dates if no schedules defined yet
                      }}
                    />
                    {editingClass?.schedules.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Only {editingClass.schedules.map((s: ClassSchedule) => DAYS_OF_WEEK[s.dayOfWeek]).join(', ')} dates can be selected
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={styles.form.label}>{t.endDate || "Class End Date"} <span className="text-gray-500 text-xs">({t.optional})</span></label>
                    <DatePicker
                      selected={editingClass?.endDate}
                      onChange={(date: Date | null) => {
                        if (!editingClass) return;
                        setEditingClass((prev: any) => ({
                          ...prev,
                          endDate: date
                        }));
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      showTimeSelect={false}
                      dateFormat="MMMM d, yyyy"
                      minDate={editingClass?.startDate}
                      isClearable={true}
                      placeholderText={t.noEndDate || "No end date"}
                    />
                  </div>
                </div>
                
                {/* Contract Section */}
                <div>
                  <label className={styles.form.label}>
                    Contract (PDF, max 5MB)
                    <span className="text-sm text-gray-500 ml-1">(Optional)</span>
                    <Tooltip text="Upload a PDF contract document for this class. This is optional but recommended for keeping track of agreements with students.">
                      <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 ml-1 inline-block" />
                    </Tooltip>
                  </label>
                  <div className="flex flex-col space-y-2">
                    {editingClass?.contractUrl && (
                      <div className="flex items-center">
                        <a 
                          href={editingClass.contractUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 mr-2 text-sm"
                        >
                          View Current Contract
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingClass((prev: any) => ({
                              ...prev!,
                              contractUrl: ''
                            }));
                          }}
                          className="p-0.5 bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                          aria-label="Delete contract"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleEditContractFileChange}
                      className="mt-1 block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-indigo-50 file:text-indigo-700
                        hover:file:bg-indigo-100"
                    />
                    <span className="text-sm text-gray-500">
                      (Upload a new file to replace)
                    </span>
                    {editContractFile && (
                      <div className="mt-2 text-sm text-gray-700 flex items-center">
                        <span>Selected file: {editContractFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setEditContractFile(null)}
                          className="p-0.5 bg-red-600 rounded-full hover:bg-red-700 transition-colors ml-2"
                          aria-label="Remove selected file"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Payment Configuration Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
                {"Payment Configuration"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={styles.form.label}>{"Payment Type"}</label>
                  <select
                    value={editingClass?.paymentConfig.type}
                    onChange={(e) => {
                      if (!editingClass) return;
                      const type = e.target.value as 'weekly' | 'monthly';
                      setEditingClass((prev: any) => {
                        if (type === 'monthly') {
                          // If switching to monthly, adjust the start date to a valid day (1, 15, or last day)
                          const currentDate = (() => {
                            const [year, month, day] = prev.paymentConfig.startDate.split('-').map(Number);
                            const date = new Date();
                            date.setFullYear(year);
                            date.setMonth(month - 1); // Month is 0-indexed in JavaScript
                            date.setDate(day);
                            return date;
                          })();
                          
                          const day = currentDate.getDate();
                          const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                          
                          // Determine the closest valid day (1, 15, or last day)
                          let newDay: number;
                          if (day <= 8) {
                            newDay = 1;
                          } else if (day <= 23) {
                            newDay = 15;
                          } else {
                            newDay = lastDayOfMonth;
                          }
                          
                          const newCurrentDate = new Date(currentDate);
                          newCurrentDate.setDate(newDay);
                          
                          const monthlyOption = newDay === 1 ? 'first' : newDay === 15 ? 'fifteen' : 'last';
                          
                          const startDateStr = newCurrentDate.toISOString().split('T')[0];
                          
                          return {
                            ...prev,
                            paymentConfig: {
                              ...prev.paymentConfig,
                              type,
                              startDate: startDateStr,
                              monthlyOption,
                              weeklyInterval: null
                            }
                          };
                        } else {
                          return {
                            ...prev,
                            paymentConfig: {
                              ...prev.paymentConfig,
                              type,
                              monthlyOption: undefined,
                              weeklyInterval: 1
                            }
                          };
                        }
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <div>
                  <label className={styles.form.label}>{"Payment Start Date"}</label>
                  <DatePicker
                    selected={(() => {
                      if (!editingClass) return new Date();
                      try {
                        const [year, month, day] = editingClass.paymentConfig.startDate.split('-').map(Number);
                        const date = new Date();
                        date.setFullYear(year);
                        date.setMonth(month - 1); // Month is 0-indexed in JavaScript
                        date.setDate(day);
                        return date;
                      } catch (e) {
                        return new Date();
                      }
                    })()}
                    onChange={(date: Date | null) => {
                      if (!editingClass || !date) return;
                      
                      // Format as YYYY-MM-DD
                      const startDateStr = date.toISOString().split('T')[0];
                      
                      // For monthly payments, automatically select the appropriate option based on the day
                      if (editingClass.paymentConfig.type === 'monthly') {
                        const day = date.getDate();
                        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                        
                        let monthlyOption: 'first' | 'fifteen' | 'last';
                        if (day === 1) monthlyOption = 'first';
                        else if (day === 15) monthlyOption = 'fifteen';
                        else if (day === lastDayOfMonth) monthlyOption = 'last';
                        else {
                          // If not a valid day, adjust to the closest valid day
                          if (day < 8) {
                            date.setDate(1);
                            monthlyOption = 'first';
                          } else if (day < 23) {
                            date.setDate(15);
                            monthlyOption = 'fifteen';
                          } else {
                            date.setDate(lastDayOfMonth);
                            monthlyOption = 'last';
                          }
                          // Update the date string after adjustment
                          const adjustedDateStr = date.toISOString().split('T')[0];
                          setEditingClass((prev: any) => ({
                            ...prev,
                            paymentConfig: {
                              ...prev.paymentConfig,
                              startDate: adjustedDateStr,
                              monthlyOption
                            }
                          }));
                          return;
                        }
                        
                        setEditingClass((prev: any) => ({
                          ...prev,
                          paymentConfig: {
                            ...prev.paymentConfig,
                            startDate: startDateStr,
                            monthlyOption
                          }
                        }));
                      } else {
                        setEditingClass((prev: any) => ({
                          ...prev,
                          paymentConfig: {
                            ...prev.paymentConfig,
                            startDate: startDateStr
                          }
                        }));
                      }
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    showTimeSelect={false}
                    dateFormat="MMMM d, yyyy"
                    filterDate={(date) => {
                      // For monthly payments, only allow selecting 1st, 15th, or last day of month
                      if (editingClass?.paymentConfig.type === 'monthly') {
                        const day = date.getDate();
                        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                        return day === 1 || day === 15 || day === lastDayOfMonth;
                      }
                      return true; // No filter for weekly payments
                    }}
                  />
                </div>
                
                {editingClass?.paymentConfig.type === 'weekly' ? (
                  <div>
                    <label htmlFor="editWeeklyInterval" className={styles.form.label}>
                      {"Payment Frequency"}
                      <span className="ml-1 inline-block" title="How often payments should be processed. For weekly payments, specify the interval in weeks. For monthly payments, choose between 1st, 15th, or last day of the month.">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                    </label>
                    <div className="flex items-center">
                      <span className="mr-2">Every</span>
                      <input
                        type="number"
                        id="editWeeklyInterval"
                        min="1"
                        value={editingClass?.paymentConfig.weeklyInterval === undefined ? '' : editingClass?.paymentConfig.weeklyInterval}
                        onChange={(e) => {
                          if (!editingClass) return;
                          const value = e.target.value;
                          setEditingClass((prev: any) => ({
                            ...prev,
                            paymentConfig: {
                              ...prev.paymentConfig,
                              weeklyInterval: value === '' ? undefined : Math.max(1, parseInt(value))
                            }
                          }));
                        }}
                        className="mt-1 w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <span className="ml-2">{editingClass?.paymentConfig.weeklyInterval === 1 ? 'week' : 'weeks'}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="editMonthlyOption" className={styles.form.label}>
                      {t.selectPaymentDay || "Payment Day"}
                      <span className="ml-1 text-gray-500 text-xs">
                        (auto-set)
                      </span>
                    </label>
                    <div className="relative">
                      <div className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 text-gray-500 px-3 py-2 sm:text-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        {editingClass?.paymentConfig.monthlyOption === 'first' && t.firstDayMonth}
                        {editingClass?.paymentConfig.monthlyOption === 'fifteen' && t.fifteenthDayMonth}
                        {editingClass?.paymentConfig.monthlyOption === 'last' && t.lastDayMonth}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Payment day is automatically set based on the selected payment start date.
                    </p>
                  </div>
                )}
                
                <div>
                  <label htmlFor="editPaymentLink" className={styles.form.label}>
                    Payment Link
                  </label>
                  <input
                    type="url"
                    id="editPaymentLink"
                    value={editingClass?.paymentConfig.paymentLink || ''}
                    onChange={(e) => {
                      if (!editingClass) return;
                      setEditingClass((prev: any) => ({
                        ...prev,
                        paymentConfig: {
                          ...prev.paymentConfig,
                          paymentLink: e.target.value
                        }
                      }));
                    }}
                    placeholder="https://payment-provider.com/your-payment-link"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a URL where students can make payments
                  </p>
                </div>
                
                <div>
                  <label htmlFor="editPaymentAmount" className={styles.form.label}>
                    Payment Amount
                  </label>
                  <input
                    type="number"
                    id="editPaymentAmount"
                    min="0"
                    step="0.01"
                    value={editingClass?.paymentConfig.amount === undefined ? '' : editingClass?.paymentConfig.amount}
                    onChange={(e) => {
                      if (!editingClass) return;
                      const value = e.target.value;
                      setEditingClass((prev: any) => ({
                        ...prev,
                        paymentConfig: {
                          ...prev.paymentConfig,
                          amount: value === '' ? undefined : parseFloat(value)
                        }
                      }));
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="editPaymentCurrency" className={styles.form.label}>
                    Currency
                  </label>
                  <select
                    id="editPaymentCurrency"
                    value={editingClass?.paymentConfig.currency || 'USD'}
                    onChange={(e) => {
                      if (!editingClass) return;
                      setEditingClass((prev: any) => ({
                        ...prev,
                        paymentConfig: {
                          ...prev.paymentConfig,
                          currency: e.target.value
                        }
                      }));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="BRL">BRL (R$)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingClass(null)}
                disabled={isSaving}
                className={`${styles.buttons.cancel} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t.cancel}
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={isSaving}
                className={`${styles.buttons.primary} ${isSaving ? 'opacity-80' : ''} flex items-center justify-center`}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {"Saving..."}
                  </>
                ) : (
                  t.save
                )}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}; 